/**
 * The control a press last landed on, for a dialog to hand the focus back to when it closes.
 *
 * A dialog used to take "whatever held the focus when it opened" as its opener. WebKit (Safari, and every iOS
 * browser, which are all WebKit) never focuses a clicked or tapped `<button>`, so there that is `<body>` and the
 * focus was lost to the top of the page on close. One capture listener on the document remembers the focusable
 * control under each press instead — `pointerdown` for a finger, a pen or a mouse, and `click` for the taps that come
 * with no pointer events (an assistive tool's activation) — and `openerCandidates` offers it to the dialog.
 *
 * A menu item is gone by the time its dialog closes (the menu shut when it was chosen), so the record also keeps
 * the item's menu trigger — the control the menu library itself gives the focus back to — as the next choice.
 *
 * A record belongs to one interaction: a key press starts another one (the focus is then where the keyboard put
 * it), and a record older than `FRESH_MS` is not offered at all, so a dialog that opens on its own minutes after a
 * tap does not send the focus to that old control.
 */

/** Everything a press can activate and the focus can go back to. */
const PRESSABLE = [
  'button', 'a[href]', 'area[href]', 'input:not([type="hidden"])', 'select', 'textarea', 'summary',
  '[role="button"]', '[role="menuitem"]', '[role="menuitemcheckbox"]', '[role="menuitemradio"]', '[role="option"]',
  '[role="tab"]', '[role="link"]', '[contenteditable]:not([contenteditable="false"])', '[tabindex]',
].join(',');

/** How long a press stays the opener of a dialog that opens after it: long enough for a read the dialog waits on. */
export const FRESH_MS = 5_000;

type Press = { control: HTMLElement; trigger: HTMLElement | null; at: number };
let last: Press | null = null;

/** The control that opened the menu `el` sits in (Radix names its content after its trigger), if any. */
function menuTriggerOf(el: Element): HTMLElement | null {
  const menu = el.closest('[role="menu"]');
  const id = menu?.getAttribute('aria-labelledby');
  if (!id) return null;
  const trigger = document.getElementById(id.split(/\s+/)[0]!);
  return trigger instanceof HTMLElement && !menu!.contains(trigger) ? trigger : null;
}

function record(event: Event) {
  // A click with no press behind it (`detail` 0) while a control holds the focus is the keyboard's: Enter in a
  // field submits its form through a click on the submit button. The focused control stays the opener. With
  // nothing focused (an assistive tool's activation) the click is the only trace, so it is kept.
  if (event.type === 'click' && (event as MouseEvent).detail === 0 && isFocusHeld()) return;
  const target = event.target;
  const control = target instanceof Element ? target.closest<HTMLElement>(PRESSABLE) : null;
  last = control === null ? null : { control, trigger: menuTriggerOf(control), at: Date.now() };
}

function forget() { last = null; }

function isFocusHeld() {
  const active = document.activeElement;
  return active !== null && active !== document.body;
}

if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', record, true);
  document.addEventListener('click', record, true);
  document.addEventListener('keydown', forget, true);
}

/**
 * Where a dialog opening now should send the focus back to, best first; the dialog takes the first one still in
 * the page when it closes. The recent press comes before the focused element: on WebKit a press inside an open
 * dialog leaves the focus on that dialog's first control, and the pressed one is the opener. With no fresh press
 * (a keyboard opening, a dialog that opens on its own) it is the focused element, unless that is the page itself.
 * Its menu trigger follows either, for an item that is gone by the close.
 */
export function openerCandidates(): HTMLElement[] {
  const found: (HTMLElement | null)[] = [];
  if (last !== null && Date.now() - last.at <= FRESH_MS) found.push(last.control, last.trigger);
  const active = document.activeElement;
  if (active instanceof HTMLElement && active !== document.body) found.push(active, menuTriggerOf(active));
  return found.filter((el, i): el is HTMLElement => el !== null && found.indexOf(el) === i);
}
